import type { LiveValue } from '../api/types.js';

/** True when `next` should replace `prev`: newer, or same instant with other content. */
export function supersedes(prev: LiveValue | null | undefined, next: LiveValue): boolean {
  if (!prev) return true;
  if (next.ts > prev.ts) return true;
  if (next.ts < prev.ts) return false;
  return next.v !== prev.v || next.vs !== prev.vs || next.q !== prev.q || next.sf !== prev.sf;
}

/** True when the displayed content differs (value, text, quality or flags). */
export function contentChanged(prev: LiveValue | null | undefined, next: LiveValue): boolean {
  if (!prev) return false;
  return next.v !== prev.v || next.vs !== prev.vs || next.q !== prev.q || next.sf !== prev.sf;
}

export interface LiveState {
  values: ReadonlyMap<string, LiveValue>;
  /** Per point, incremented when a pushed update changed the shown content (drives the flash). */
  changes: ReadonlyMap<string, number>;
}

export const EMPTY_LIVE_STATE: LiveState = { values: new Map(), changes: new Map() };

/**
 * Merge pushed updates into the state. Older or duplicate values are ignored;
 * the same object is returned when nothing changed, so React skips a render.
 * `baseline` gives the value already shown for a point not yet in the state
 * (from a REST response): updates older than it are ignored and a change
 * against it counts for the flash.
 */
export function mergeLive(
  state: LiveState,
  updates: readonly LiveValue[],
  baseline: (pointId: string) => LiveValue | null | undefined = () => undefined,
): LiveState {
  let values: Map<string, LiveValue> | null = null;
  let changes: Map<string, number> | null = null;
  for (const u of updates) {
    const current = newest(baseline(u.point_id), (values ?? state.values).get(u.point_id));
    if (!supersedes(current, u)) continue;
    values ??= new Map(state.values);
    values.set(u.point_id, u);
    if (contentChanged(current, u)) {
      changes ??= new Map(state.changes);
      changes.set(u.point_id, (changes.get(u.point_id) ?? 0) + 1);
    }
  }
  if (!values) return state;
  return { values, changes: changes ?? state.changes };
}

/** The newer of two values for one point. */
export function newest(
  a: LiveValue | null | undefined,
  b: LiveValue | null | undefined,
): LiveValue | null {
  if (!a) return b ?? null;
  if (!b) return a;
  return b.ts > a.ts ? b : a;
}
